import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { postJson } from "../api";
import { formatText, useTexts, type Label } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead, TextField, useToast } from "../ui/kit";
import {
  ListFooter,
  pageSizes,
  useServerList,
  useSort,
  type InitialPage,
  type Page,
  type ServerList,
} from "../ui/list";
import { PageState, PasswordField, ServiceState } from "../ui/pieces";
import { connectionTone, errorText, plural, routeName, usableOf, when, type AiError } from "./ai";
import { ApiKeyWindow } from "./ApiKeyWindow";
import { ModelsGrid } from "./ModelsGrid";
import { useAiData, useTitle } from "./parts";
import { joinAnd } from "./ProvidersPage";
import {
  DataTable,
  MobileActions,
  NoResults,
  RowMenu,
  RowsSkeleton,
  SearchBox,
  TableBar,
  Window,
  useFilters,
  useIsMobile,
  type MenuItem,
  type MenuReason,
  type SortColumn,
} from "./widgets";
import styles from "./ai.module.css";

type Key = {
  connectionId: number;
  name: string;
  status: string;
  state: string;
  hasSecret: boolean;
  lastSuccessAt: string | null;
  /** «Última validación»: el último intento con la key, correcto o fallido (C55 1.14.12, E126). */
  lastCheckedAt?: string | null;
  consecutiveFailures: number;
  hasHealthAlert: boolean;
  lastFailureCode: string | null;
  endpoint: string | null;
  keyHint: string | null;
  usedBy: string[];
  usedByOrganizations: number;
};

type ProviderDetail = {
  providerId: number;
  code: string;
  name: string;
  canConnect: boolean;
  state: string;
  custom: boolean;
  /** «N usables de M» del subtítulo: las píldoras «Usables · Todos» de la pestaña Modelos (regla única, E107). */
  usableModels: number;
  models: number;
  lastSyncedAt: string | null;
  lastValidatedAt: string | null;
  defaults: { routeCode: string; modelName: string }[];
  /** Cuántas API keys tiene (sin traerlas: la grilla las pide por página). */
  keyCount: number;
  /** API key con la que «Actualizar modelos» de la pestaña Modelos trae el catálogo (la activa probada, si hay). */
  syncKeyId: number | null;
  /** API keys que fallan (las que más fallan, a lo sumo 5) para el aviso C14 bajo el encabezado. */
  alerts: Key[];
  /** Puede haber más API keys que fallan que las de `alerts`. */
  alertsMore: boolean;
  /** La página de la grilla de API keys pedida con la ficha (`keys=1`); null si no se pidió. */
  keys: Page<Key> | null;
};

type Tab = "keys" | "modelos";

/** Columnas de las API keys que se ordenan en el servidor (C28). */
const keySorts = ["name", "validated", "failures"] as const;

/** Más API keys que esto: la grilla lleva buscador (C06, más de 6 opciones). */
const searchFrom = 6;

/** Dirección de una pestaña de la ficha (V23: cada nivel en la URL). */
export const providerHref = (code: string, tab: Tab = "keys") =>
  `/Ai/Providers/${encodeURIComponent(code)}${tab === "modelos" ? "?tab=modelos" : ""}`;

type Result = { tone?: "done" | "error" | "info"; messageKey?: string; args?: (string | number)[] } & AiError;
type Dialog = { kind: "test" | "change" | "endpoint" | "disable" | "delete"; key: Key } | null;

/** El motivo de una falla en palabras (nunca el código del proveedor, D7). */
const reason = (L: Label, code: string | null) =>
  L(/AUTH|401|403|KEY/i.test(code ?? "") ? "ADM_AI_REASON_AUTH" : "ADM_AI_REASON_PROVIDER");

const failing = (key: Key) => key.state === "CONNECTION_ERROR" && key.consecutiveFailures > 0;

/** «Última validación»: el último intento (del BFF); sin ese dato, el último éxito. */
const checkedAt = (key: Key) => key.lastCheckedAt ?? key.lastSuccessAt;

/** El motivo dentro de una frase: solo la primera letra en minúscula («la API key no es válida», no «api key»). */
const lowerFirst = (text: string) => text.charAt(0).toLocaleLowerCase() + text.slice(1);

/** Una ruta o una empresa responde con esta API key: no se desactiva ni se elimina sin cambiarla antes en Rutas. */
const inUse = (key: Key) => key.usedBy.length > 0 || key.usedByOrganizations > 0;

/**
 * La acción que la fila muestra a la vista (C28 1.14.11, R7 opción B): «Validar catálogo» si la key funciona, «Cambiar API
 * key» si falla o no tiene clave; una key inactiva con clave no muestra ninguna. El ⋮ de escritorio no la repite (E116).
 */
const visibleAction = (key: Key): "validate" | "change" | null =>
  // Una key que nunca respondió queda «Por verificar» aunque falle: sus fallos seguidos también cuentan.
  !key.hasSecret || failing(key) || key.consecutiveFailures > 0
    ? "change"
    : key.status === "ACTIVE"
      ? "validate"
      : null;

/**
 * IA › Proveedores › ← proveedor (§5.9 `ia-proveedor`), ficha con pestañas (Guía de patrones, Admin: «un proveedor»;
 * V23, `?tab=`): «API keys» y «Modelos». API keys: grilla C28 paginada, buscada y ordenada en el servidor con su estado,
 * sus últimos 4 caracteres, dónde responde, la última validación y los fallos seguidos, con su acción a la vista y un ⋮
 * en tres grupos (C11 1.14.11): comprobar (Validar catálogo, Probar), cambiar (Cambiar API key, Dirección del servicio) y
 * quitar (Desactivar o Activar, Eliminar). Modelos: la misma grilla de IA › Modelos (`ModelsGrid`) solo con sus modelos,
 * con la última actualización y el uso por defecto arriba. El encabezado no lleva ⋮: a la derecha del título va solo la
 * acción de la pestaña abierta («Agregar API key» o «Actualizar modelos», E117), en el mismo lugar en las dos (E120).
 * Una API key se agrega aquí, dentro de su proveedor (no hay «conectar proveedor», decisión del dueño 2026-10-30).
 * Probar confirma que consume uso; desactivar confirma y se deshace (D8); eliminar confirma y no se deshace (C12).
 */
export function ProviderPage({ code }: { code: string }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "modelos" ? "modelos" : "keys";
  const filters = useFilters();
  const search = filters.get("search");
  const order = useSort(keySorts);
  const keysQuery = [search ? `search=${encodeURIComponent(search)}` : "", order.query].filter(Boolean).join("&");
  const keysUrl = `/api/admin/ai/providers/${encodeURIComponent(code)}/keys${keysQuery ? `?${keysQuery}` : ""}`;
  // Al abrir en la pestaña API keys, la ficha trae en el mismo pedido la página de su grilla (la de la dirección; en el
  // celular, la primera): abrir es un solo viaje. Se decide una vez; después la grilla pide sus páginas.
  const [firstKeys] = useState(() => {
    if (tab !== "keys") return null;
    const pageNow = window.matchMedia("(max-width: 767px)").matches ? 1 : Math.max(1, Number(params.get("page")) || 1);
    const sizeParam = Number(params.get("pageSize"));
    const sizeNow = (pageSizes as readonly number[]).includes(sizeParam) ? sizeParam : 10;
    return {
      url: keysUrl,
      query: [`keys=1`, keysQuery, `page=${pageNow}`, `pageSize=${sizeNow}`].filter(Boolean).join("&"),
    };
  });
  const reloadKeys = useRef<() => void>(() => undefined);
  const { data, state, reload, retry } = useAiData<ProviderDetail>(
    `/api/admin/ai/providers/${encodeURIComponent(code)}${firstKeys ? `?${firstKeys.query}` : ""}`,
  );
  const [connect, setConnect] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [newKey, setNewKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [error, setError] = useState<{ text: string; field?: string | null } | null>(null);
  // Tras «Actualizar modelos» la grilla de la pestaña Modelos se vuelve a pedir (cambia su versión).
  const [gridVersion, setGridVersion] = useState(0);
  useTitle(data ? `${data.name} · ${L(tab === "modelos" ? "ADM_AI_MODELS" : "ADM_AI_KEYS")}` : L("ADM_AI_PROVIDERS"));

  // «Agregar clave» desde Inicio o /Ai/Conexiones/AgregarClave (§4): ?conectar=1 abre la ventana tras el foco del título.
  const wanted = params.get("conectar");
  useEffect(() => {
    if (!wanted) return;
    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setConnect(true);
        setParams({}, { replace: true });
      }),
    );
    return () => cancelAnimationFrame(frame);
  }, [wanted, setParams]);

  const back = { href: "/Ai/Providers/Index", label: L("ADM_AI_PROVIDERS") };
  if (state === "missing")
    return (
      <PageState
        head={<PageHead title={L("ADM_AI_PROVIDER")} back={back} />}
        title={L("ADM_AI_PROVIDER_MISSING")}
        line={L("ADM_AI_PROVIDER_MISSING_LINE")}
        action={
          <a className="tm-btn secondary" href="/Ai/Providers/Index">
            {L("ADM_AI_GO_PROVIDERS")}
          </a>
        }
      />
    );
  if (state === "failed")
    return (
      <PageState
        head={<PageHead title={L("ADM_AI_PROVIDER")} back={back} />}
        title={L("ADM_UNAVAILABLE")}
        line={L("ADM_UNAVAILABLE_LINE")}
        action={
          <button type="button" className="tm-btn secondary" onClick={retry}>
            {L("ADM_RETRY")}
          </button>
        }
      />
    );
  if (!data)
    return (
      <>
        <PageHead title={L("ADM_AI_PROVIDER")} back={back} />
        <RowsSkeleton rows={3} />
      </>
    );

  const tone = connectionTone(L, data.state);
  /** Después de una acción: el encabezado (avisos, última validación) y la página de API keys a la vista. */
  const refresh = () => {
    void reload();
    reloadKeys.current();
  };
  /** Un pedido de la ficha: sin red, responde como un error (status 0) y nunca deja un botón cargando para siempre. */
  const call = async (name: string, url: string, body: unknown = {}) => {
    setBusy(name);
    try {
      return await postJson<Result>(url, body);
    } catch {
      return { status: 0, body: null };
    } finally {
      setBusy(null);
    }
  };
  const messageOf = (reply: { status: number; body: Result | null }) =>
    reply.status === 200 && reply.body?.messageKey === "CONN_TEST_OK"
      ? formatText(L("ADM_AI_TEST_OK"), String(reply.body.args?.[0] ?? ""))
      : formatText(L(reply.body?.messageKey || "ADMIN_ERR_GENERIC"), ...(reply.body?.args ?? []));
  const validate = async (key: Key) => {
    const reply = await call(`validate-${key.connectionId}`, `/api/admin/ai/connections/${key.connectionId}/validate`);
    toast.show(messageOf(reply));
    refresh();
  };
  const sync = async (connectionId: number) => {
    const reply = await call(`sync-${connectionId}`, `/api/admin/ai/connections/${connectionId}/sync`);
    const body = reply.body as unknown as { failed?: boolean; added?: number; updated?: number } | null;
    toast.show(
      reply.status === 200 && body && !body.failed
        ? formatText(
            L("ADM_AI_SYNC_DONE"),
            data.name,
            (body.added ?? 0).toLocaleString(locale),
            (body.updated ?? 0).toLocaleString(locale),
          )
        : formatText(L("ADM_AI_SYNC_FAILED"), data.name),
    );
    refresh();
    setGridVersion((version) => version + 1);
  };
  const setStatus = async (key: Key, next: "ACTIVE" | "INACTIVE", undo = true) => {
    const reply = await call(`status-${key.connectionId}`, `/api/admin/ai/connections/${key.connectionId}/status`, {
      status: next,
    });
    if (reply.status === 200) {
      setDialog(null);
      toast.show(
        L(next === "ACTIVE" ? "ADM_AI_KEY_ACTIVATED" : "ADM_AI_KEY_DISABLED"),
        undo
          ? { label: L("ADM_UNDO"), run: () => void setStatus(key, next === "ACTIVE" ? "INACTIVE" : "ACTIVE", false) }
          : undefined,
      );
    } else if (dialog) setError({ text: errorText(L, reply.body) });
    else toast.show(errorText(L, reply.body));
    refresh();
  };
  const open = (kind: NonNullable<Dialog>["kind"], key: Key) => {
    setError(null);
    setNewKey("");
    setEndpoint(key.endpoint ?? "");
    setDialog({ kind, key });
  };
  const close = () => {
    setDialog(null);
    setNewKey("");
  };
  const runTest = async () => {
    if (!dialog) return;
    const reply = await call("test", `/api/admin/ai/connections/${dialog.key.connectionId}/test`);
    if (reply.status === 200 && reply.body?.tone === "done") {
      close();
      toast.show(messageOf(reply));
    } else setError({ text: messageOf(reply) });
    refresh();
  };
  const changeKey = async () => {
    if (!dialog) return;
    if (!newKey.trim()) {
      setError({ text: L("CONNECT_NEED_KEY"), field: "apiKey" });
      return;
    }
    const reply = await call("key", `/api/admin/ai/connections/${dialog.key.connectionId}/key`, { apiKey: newKey });
    if (reply.status === 200 && reply.body?.tone === "done") {
      close();
      toast.show(L("ADM_AI_KEY_CHANGED"));
    } else setError({ text: messageOf(reply), field: reply.body?.field });
    refresh();
  };
  const remove = async () => {
    if (!dialog) return;
    const reply = await call("delete", `/api/admin/ai/connections/${dialog.key.connectionId}/delete`);
    if (reply.status === 200) {
      close();
      toast.show(formatText(L("ADM_AI_KEY_DELETED"), dialog.key.name));
    } else setError({ text: errorText(L, reply.body) });
    refresh();
  };
  const saveEndpoint = async () => {
    if (!dialog) return;
    const reply = await call("endpoint", `/api/admin/ai/connections/${dialog.key.connectionId}/endpoint`, { endpoint });
    if (reply.status === 200) {
      close();
      toast.show(L("ADM_AI_ENDPOINT_SAVED"));
      refresh();
    } else setError({ text: errorText(L, reply.body), field: reply.body?.field });
  };

  /** «Texto y Visión y 2 empresas»: dónde responde la API key. */
  const usedText = (key: Key) => {
    const places = key.usedBy.map((route) => routeName(L, route));
    if (key.usedByOrganizations > 0)
      places.push(plural(L, locale, key.usedByOrganizations, "ADM_AI_N_COMPANIES_ONE", "ADM_AI_N_COMPANIES"));
    return joinAnd(L, places);
  };
  /** Línea del celular (C17): cuál es y dónde responde, después su salud (se recorta solo al final). */
  const keyLine = (key: Key) => {
    const parts = [
      key.keyHint
        ? formatText(L("ADM_AI_KEY_ENDS_IN_SHORT"), key.keyHint)
        : L(key.hasSecret ? "ADM_AI_KEY_SAVED" : "ADM_AI_KEY_MISSING"),
      inUse(key) ? formatText(L("ADM_AI_KEY_USED_IN"), usedText(key)) : L("ADM_AI_KEY_NOT_USED"),
    ];
    if (failing(key)) parts.push(lowerFirst(reason(L, key.lastFailureCode)));
    const checked = checkedAt(key);
    if (checked && !failing(key)) parts.push(formatText(L("ADM_AI_KEY_VALIDATED"), when(L, locale, checked, true)));
    // C17: un dato que no aporta no va en la fila («0 fallos seguidos»); la columna de escritorio sí lo muestra.
    if (key.hasSecret && key.consecutiveFailures > 0)
      parts.push(plural(L, locale, key.consecutiveFailures, "ADM_AI_N_FAILURES_ONE", "ADM_AI_N_FAILURES"));
    return parts.join(" · ");
  };
  /**
   * El ⋮ de la fila en sus tres grupos (C11 1.14.11). En escritorio no lleva la acción que la fila muestra a la vista;
   * en el celular (hoja C27) lleva todas (E103). «Actualizar modelos» ya no está aquí: cambia el catálogo del proveedor y
   * va en su pestaña Modelos (decisión del dueño, R5).
   */
  const menu = (key: Key, all: boolean): MenuItem[] => {
    const shown = all ? null : visibleAction(key);
    const items: MenuItem[] = [];
    if (key.hasSecret) {
      if (shown !== "validate")
        items.push({
          label: L("ADM_AI_VALIDATE_CATALOG"),
          icon: "list-checks",
          group: "comprobar",
          onSelect: () => void validate(key),
        });
      items.push({
        label: L("ADM_AI_TEST_WITH_USAGE"),
        icon: "flask-conical",
        group: "comprobar",
        onSelect: () => open("test", key),
      });
    }
    // Una API key sin key (solo las desactivadas que ya respondieron antes del script 164) se completa con «Cambiar».
    if (shown !== "change")
      items.push({
        label: L("ADM_AI_CHANGE_KEY_MENU"),
        icon: "key-round",
        group: "cambiar",
        onSelect: () => open("change", key),
      });
    items.push({
      label: L("ADM_AI_ENDPOINT_MENU"),
      icon: "link",
      group: "cambiar",
      onSelect: () => open("endpoint", key),
    });
    if (key.status === "ACTIVE")
      items.push({
        label: L("ADM_AI_DISABLE_MENU"),
        icon: "power",
        group: "quitar",
        onSelect: () => open("disable", key),
        disabled: inUse(key),
      });
    else if (key.hasSecret)
      items.push({
        label: L("ADM_AI_ACTIVATE"),
        icon: "power",
        group: "quitar",
        onSelect: () => void setStatus(key, "ACTIVE"),
      });
    items.push({
      label: L("ADM_AI_DELETE_MENU"),
      icon: "trash-2",
      group: "quitar",
      onSelect: () => open("delete", key),
      danger: true,
      disabled: inUse(key),
    });
    return items;
  };
  /** Desactivar y Eliminar en uso comparten un solo motivo con su salida a Rutas (E119). */
  const reasons = (key: Key): MenuReason[] =>
    inUse(key)
      ? [
          {
            group: "quitar",
            text: formatText(L("ADM_AI_KEY_USED_BY"), usedText(key)),
            link: { label: L("ADM_AI_GO_ROUTES"), href: "/Ai/Rutas" },
          },
        ]
      : [];
  /** La acción de la fila a la vista en escritorio, sin fondo hasta el hover de la fila (en el celular va en la hoja). */
  const rowAction = (key: Key) => {
    const action = visibleAction(key);
    if (action === "change")
      return (
        <button type="button" className={styles.rowGhost} data-part="accion-fila" onClick={() => open("change", key)}>
          {L("ADM_AI_CHANGE_KEY")}
        </button>
      );
    if (action !== "validate") return null;
    const working = busy === `validate-${key.connectionId}`;
    return (
      <button
        type="button"
        className={styles.rowGhost}
        data-part="accion-fila"
        aria-busy={working || undefined}
        onClick={() => !working && void validate(key)}
      >
        {L("ADM_AI_VALIDATE_CATALOG")}
      </button>
    );
  };
  const keyMenu = (key: Key) => (
    <RowMenu
      label={`${L("ADM_MORE_ACTIONS")}: ${key.name}`}
      title={key.name}
      items={menu(key, mobile)}
      reasons={reasons(key)}
      reveal={!mobile}
    />
  );
  const dash = <span className={styles.muted}>—</span>;
  const keySortColumns: SortColumn[] = [
    { key: "name", title: L("ADM_AI_NAME"), kind: "text" },
    { key: "validated", title: L("ADM_AI_COL_VALIDATED"), kind: "date" },
    { key: "failures", title: L("ADM_AI_COL_FAILURES"), kind: "number" },
  ];
  const addKey = data.canConnect ? (
    <button
      type="button"
      className={`tm-btn primary ${styles.tabAction}`}
      data-part="accion-pestana"
      onClick={() => setConnect(true)}
    >
      {L("ADM_ADD_KEY")}
    </button>
  ) : null;
  // «Actualizar modelos» trae el catálogo del proveedor con una API key que sirva (syncKeyId); sin ella, desactivado y
  // con su motivo. Mientras trabaja, el indicador ocupa el lugar del ícono: mismo texto y ancho, y no acepta otro clic
  // (C04 1.14.12).
  const syncing = data.syncKeyId != null && busy === `sync-${data.syncKeyId}`;
  const syncModels = (
    <button
      type="button"
      className={`tm-btn secondary ${styles.tabAction} ${styles.syncButton}`}
      data-part="accion-pestana"
      disabled={data.syncKeyId == null}
      title={data.syncKeyId == null ? L("ADM_AI_SYNC_NEEDS_KEY") : undefined}
      aria-busy={syncing || undefined}
      onClick={() => data.syncKeyId != null && busy === null && void sync(data.syncKeyId)}
    >
      {/* Cargando, el indicador común de aria-busy (kit) toma el lugar de refresh-cw: un solo indicador (E124). */}
      <ChatIcon name="refresh-cw" />
      {L("ADM_AI_SYNC_MODELS")}
    </button>
  );
  /** «Uso por defecto»: una línea por modelo con sus rutas juntas («Texto y Visión: deepseek-flash»). */
  const defaultUse = [...new Set(data.defaults.map((item) => item.modelName))].map((model) => ({
    model,
    routes: joinAnd(
      L,
      data.defaults.filter((item) => item.modelName === model).map((item) => routeName(L, item.routeCode)),
    ),
  }));

  const keysTab = (keys: ServerList<Key>) =>
    data.keyCount === 0 ? (
      <div className="tm-empty" role="status">
        <strong>{L("ADM_AI_NO_KEYS")}</strong>
        <span>{L("ADM_AI_NO_KEYS_LINE")}</span>
        {data.canConnect ? (
          <button type="button" className="tm-btn secondary" onClick={() => setConnect(true)}>
            {L("ADM_ADD_KEY")}
          </button>
        ) : null}
      </div>
    ) : (
      <>
        {data.keyCount > searchFrom || search ? (
          <div className={styles.toolbar}>
            <SearchBox
              id="keys-search"
              value={search}
              placeholder={L("ADM_AI_SEARCH_KEY")}
              onChange={(value) => filters.set({ search: value })}
            />
          </div>
        ) : null}
        {keys.state === "loading" ? (
          <RowsSkeleton rows={Math.min(6, data.keyCount)} table={!mobile} />
        ) : keys.state === "error" || keys.state === "notFound" || keys.state === "forbidden" ? (
          <Notice
            tone="error"
            action={
              <button type="button" className="tm-btn secondary" onClick={keys.reload}>
                {L("ADM_RETRY")}
              </button>
            }
          >
            {L("ADM_UNAVAILABLE_LINE")}
          </Notice>
        ) : keys.items.length === 0 ? (
          <NoResults query={search} things={L("ADM_AI_KEYS_THINGS")} onClear={() => filters.set({ search: "" })} />
        ) : (
          <>
            {/* Celular (H9): sin contador; el ⋮ de la lista solo lleva «Ordenar por», así que con una key no va. */}
            {mobile && keys.total < 2 ? null : (
              <TableBar
                count={plural(L, locale, keys.total, "ADM_AI_N_KEYS_ONE", "ADM_AI_N_KEYS")}
                columns={keySortColumns}
                sort={order.sort}
                onSort={order.set}
                hideCount={mobile}
              />
            )}
            <DataTable<Key>
              label={L("ADM_AI_KEYS")}
              rows={keys.items}
              rowKey={(key) => key.connectionId}
              sort={order.sort}
              onSort={order.set}
              hoverRows
              columns={[
                {
                  key: "name",
                  title: L("ADM_AI_NAME"),
                  sort: { key: "name", kind: "text" },
                  render: (key) => {
                    const keyTone = connectionTone(L, key.state);
                    return (
                      <span className={styles.keyCell}>
                        <span className={`${styles.mark} ${styles[`mark_${keyTone.tone}`] ?? ""}`} aria-hidden="true">
                          <ChatIcon name="key-round" />
                        </span>
                        <strong title={key.name}>{key.name}</strong>
                      </span>
                    );
                  },
                },
                {
                  key: "state",
                  title: L("ADM_AI_COL_STATUS"),
                  render: (key) => {
                    const keyTone = connectionTone(L, key.state);
                    return <ServiceState tone={keyTone.tone}>{keyTone.text}</ServiceState>;
                  },
                },
                {
                  key: "hint",
                  title: L("ADM_AI_COL_KEY_HINT"),
                  render: (key) => (key.keyHint ? `…${key.keyHint}` : dash),
                },
                {
                  key: "used",
                  title: L("ADM_AI_COL_USED_IN"),
                  render: (key) => {
                    // C28: «Usada en» vacía se escribe «—».
                    if (!inUse(key)) return dash;
                    const text = usedText(key);
                    return (
                      <span className={styles.cellEllipsis} title={text}>
                        {text}
                      </span>
                    );
                  },
                },
                {
                  key: "validated",
                  title: L("ADM_AI_COL_VALIDATED"),
                  sort: { key: "validated", kind: "date" },
                  render: (key) => {
                    const checked = checkedAt(key);
                    return checked ? when(L, locale, checked) : dash;
                  },
                },
                {
                  key: "failures",
                  title: L("ADM_AI_COL_FAILURES"),
                  numeric: true,
                  sort: { key: "failures", kind: "number" },
                  render: (key) => (key.hasSecret ? key.consecutiveFailures.toLocaleString(locale) : dash),
                },
                {
                  key: "actions",
                  title: "",
                  actions: true,
                  render: (key) => (
                    <span className={styles.rowActions}>
                      {rowAction(key)}
                      {keyMenu(key)}
                    </span>
                  ),
                },
              ]}
              mobile={(key) => {
                const keyTone = connectionTone(L, key.state);
                return {
                  // H9: la fila de 64 px con el ícono de la key de 28 px en el color de su estado.
                  lead: (
                    <span className={`${styles.mark} ${styles[`mark_${keyTone.tone}`] ?? ""}`} aria-hidden="true">
                      <ChatIcon name="key-round" />
                    </span>
                  ),
                  title: key.name,
                  tag: <ServiceState tone={keyTone.tone}>{keyTone.text}</ServiceState>,
                  line: keyLine(key),
                  end: keyMenu(key),
                };
              }}
            />
            <ListFooter list={keys} />
          </>
        )}
      </>
    );

  return (
    <>
      <PageHead
        title={data.name}
        back={back}
        tag={<ServiceState tone={tone.tone}>{tone.text}</ServiceState>}
        sub={`${
          data.lastValidatedAt
            ? formatText(L("ADM_AI_VALIDATED_CAP"), when(L, locale, data.lastValidatedAt, true))
            : L("ADM_AI_NEVER_VALIDATED")
        } · ${usableOf(L, locale, data.usableModels, data.models)}`}
      >
        {tab === "keys" ? addKey : syncModels}
      </PageHead>
      {data.canConnect ? null : <Notice tone="info">{formatText(L("ADM_AI_CANNOT_CONNECT"), data.name)}</Notice>}
      {data.alerts.length === 1 ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={() => open("change", data.alerts[0]!)}>
              {L("ADM_AI_CHANGE_KEY")}
            </button>
          }
        >
          {formatText(
            L(data.alerts[0]!.consecutiveFailures === 1 ? "ADM_AI_KEY_FAILING_ONE" : "ADM_AI_KEY_FAILING"),
            data.alerts[0]!.name,
            data.alerts[0]!.consecutiveFailures.toLocaleString(locale),
            lowerFirst(reason(L, data.alerts[0]!.lastFailureCode)),
          )}
        </Notice>
      ) : data.alerts.length > 1 ? (
        // C14: un solo aviso por zona. Con varias, sus nombres y la salida que las muestra (orden por fallos).
        <Notice
          tone="error"
          action={
            <a className="tm-btn secondary" href={`${providerHref(data.code)}?sort=failures&dir=desc`}>
              {L("ADM_AI_SEE_FAILING")}
            </a>
          }
        >
          {formatText(
            L("ADM_AI_KEYS_FAILING_MANY"),
            `${joinAnd(
              L,
              data.alerts.slice(0, 3).map((key) => key.name),
            )}${data.alertsMore || data.alerts.length > 3 ? "…" : ""}`,
          )}
        </Notice>
      ) : null}
      <nav className={`tm-tabs ${styles.tabs}`} aria-label={L("ADM_AI_PROVIDER_TABS")} data-part="pestanas">
        {(["keys", "modelos"] as const).map((value) => (
          <a key={value} href={providerHref(data.code, value)} aria-current={value === tab ? "page" : undefined}>
            {L(value === "keys" ? "ADM_AI_KEYS" : "ADM_AI_MODELS")}
          </a>
        ))}
      </nav>
      {tab === "keys" ? (
        <>
          {/* La descripción queda en escritorio (H9). */}
          {mobile ? null : <p className={styles.tabLine}>{L("ADM_AI_KEYS_LINE")}</p>}
          <KeysList
            url={keysUrl}
            initial={firstKeys && data.keys ? { url: firstKeys.url, page: data.keys } : null}
            register={(fn) => {
              reloadKeys.current = fn;
            }}
          >
            {keysTab}
          </KeysList>
        </>
      ) : (
        <>
          <dl className={styles.facts} data-part="resumen-modelos">
            <div>
              <dt>{L("ADM_AI_LAST_SYNC")}</dt>
              <dd>{data.lastSyncedAt ? when(L, locale, data.lastSyncedAt) : L("ADM_AI_NEVER_SYNCED_CAP")}</dd>
            </div>
            <div>
              <dt>{L("ADM_AI_DEFAULT_USE")}</dt>
              <dd>
                {defaultUse.length === 0
                  ? L("ADM_AI_NO_DEFAULT_USE")
                  : defaultUse.map((item) => (
                      <span key={item.model} className={styles.block}>
                        {item.routes}: {item.model}
                      </span>
                    ))}
              </dd>
            </div>
          </dl>
          <ModelsGrid key={gridVersion} scopeProvider={data.code} />
        </>
      )}

      {tab === "keys" ? (
        addKey ? (
          <MobileActions>{addKey}</MobileActions>
        ) : null
      ) : (
        <MobileActions>{syncModels}</MobileActions>
      )}
      <ApiKeyWindow
        open={connect}
        provider={{ code: data.code, name: data.name }}
        firstKey={data.keyCount === 0}
        onClose={() => {
          setConnect(false);
          refresh();
        }}
      />
      <Window
        open={dialog?.kind === "test"}
        confirm
        title={formatText(L("ADM_AI_TEST_TITLE"), dialog?.key.name ?? "")}
        onClose={close}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={close} disabled={busy === "test"}>
              {L("ACTION_CANCEL")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy === "test"}
              aria-busy={busy === "test" || undefined}
              onClick={() => void runTest()}
            >
              {busy === "test" ? L("NPA_AI_TESTING") : L("ADM_AI_TEST")}
            </button>
          </>
        }
      >
        <p className="tm-confirm-line">{L("ADM_AI_TEST_LINE")}</p>
        {error ? (
          <p className="tm-field-error" role="alert">
            {error.text}
          </p>
        ) : null}
      </Window>
      <Window
        open={dialog?.kind === "change"}
        title={formatText(L("ADM_AI_CHANGE_KEY_TITLE"), dialog?.key.name ?? "")}
        onClose={close}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={close}>
              {L("ACTION_CANCEL")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy === "key"}
              aria-busy={busy === "key" || undefined}
              onClick={() => void changeKey()}
            >
              {busy === "key" ? L("NPA_AI_TESTING") : L("ADM_AI_TEST_AND_SAVE")}
            </button>
          </>
        }
      >
        <form
          className={styles.windowBlock}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void changeKey();
          }}
        >
          <Notice tone="info">{L("ADM_AI_CHANGE_KEY_NOTE")}</Notice>
          {error && error.field !== "apiKey" ? <Notice tone="error">{error.text}</Notice> : null}
          <PasswordField
            id="change-key"
            label={L("ADM_AI_NEW_KEY")}
            value={newKey}
            onChange={(value) => {
              setNewKey(value);
              if (error?.field === "apiKey" && value.trim()) setError(null);
            }}
            autoComplete="new-password"
            error={error?.field === "apiKey" ? error.text : null}
          />
        </form>
      </Window>
      <Window
        open={dialog?.kind === "endpoint"}
        title={L("ADM_AI_ENDPOINT_TITLE")}
        onClose={close}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={close}>
              {L("ACTION_CANCEL")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy === "endpoint"}
              aria-busy={busy === "endpoint" || undefined}
              onClick={() => void saveEndpoint()}
            >
              {L("ADM_AI_SAVE")}
            </button>
          </>
        }
      >
        <form
          className={styles.windowBlock}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void saveEndpoint();
          }}
        >
          {error && error.field !== "endpoint" ? <Notice tone="error">{error.text}</Notice> : null}
          <TextField
            id="key-endpoint"
            type="url"
            label={L("ADM_AI_ENDPOINT")}
            value={endpoint}
            maxLength={500}
            placeholder="https://"
            help={L("ADM_AI_ENDPOINT_HELP")}
            error={error?.field === "endpoint" ? error.text : null}
            onChange={(value) => {
              setEndpoint(value);
              if (error?.field === "endpoint") setError(null);
            }}
          />
        </form>
      </Window>
      <Window
        open={dialog?.kind === "disable"}
        confirm
        title={formatText(L("ADM_AI_DISABLE_TITLE"), dialog?.key.name ?? "")}
        onClose={close}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={close}>
              {L("ACTION_CANCEL")}
            </button>
            <button
              type="button"
              className={`tm-btn primary ${styles.dangerBtn}`}
              disabled={busy !== null}
              aria-busy={busy?.startsWith("status") || undefined}
              onClick={() => dialog && void setStatus(dialog.key, "INACTIVE")}
            >
              {L("ADM_AI_DISABLE")}
            </button>
          </>
        }
      >
        <p className="tm-confirm-line">{L("ADM_AI_DISABLE_LINE")}</p>
        {error ? (
          <p className="tm-field-error" role="alert">
            {error.text}
          </p>
        ) : null}
      </Window>
      <Window
        open={dialog?.kind === "delete"}
        confirm
        title={formatText(L("ADM_AI_DELETE_TITLE"), dialog?.key.name ?? "")}
        onClose={close}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={close}>
              {L("ACTION_CANCEL")}
            </button>
            <button
              type="button"
              className={`tm-btn primary ${styles.dangerBtn}`}
              disabled={busy !== null}
              aria-busy={busy === "delete" || undefined}
              onClick={() => void remove()}
            >
              {L("ADM_AI_DELETE")}
            </button>
          </>
        }
      >
        <p className="tm-confirm-line">{L("ADM_AI_DELETE_LINE")}</p>
        {error ? (
          <p className="tm-field-error" role="alert">
            {error.text}
          </p>
        ) : null}
      </Window>
    </>
  );
}

/**
 * La grilla de API keys con su lista del servidor: arranca con la página que trajo la ficha (`initial`, un solo viaje al
 * abrir) y después pide sus páginas, búsquedas y órdenes. `register` deja a la ficha recargarla tras una acción.
 */
function KeysList({
  url,
  initial,
  register,
  children,
}: {
  url: string;
  initial: InitialPage<Key> | null;
  register: (reload: () => void) => void;
  children: (list: ServerList<Key>) => ReactNode;
}) {
  const list = useServerList<Key>(url, "page", initial);
  // Sin devolver nada: lo que devuelve el efecto es su limpieza (devolver `reload` recargaba en cada dibujo).
  useEffect(() => {
    register(list.reload);
  });
  return <>{children(list)}</>;
}
